import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
// prettier-ignore
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { TowFinalisationCheckResult } from '@cpr/shared/finalisation';
import { ConfirmationDialogComponent } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/confirmation-dialog/confirmation.component';
import { ITableSortDirection } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/table/table-sort.interface';
import { take } from 'rxjs';
import { SharedModule } from '../../../../../shared/shared.module';
import { SampleStatus } from '../../../samples/components/sample-status/sample-status.component';
import type { ISample } from '../../../samples/interface/sample.interface';
import { SamplesModule } from '../../../samples/samples.module';
import { SamplesService } from '../../../samples/service/samples.service';
import type { IRecordingEvent } from '../../../tows/interface/recording-event.interface';
import { TowsService } from '../../../tows/service/tows.service';
import { TowsModule } from '../../../tows/tows.module';
import { TowLogModule } from '../../../tow-log/tow-log.module';
import { UsersModule } from '../../../users/users.module';
import { FinalisationReport } from '../finalisation-report/finalisation-report.component';

export interface FinaliseTowDialogData {
  towId: string;
  tow: IRecordingEvent<'tow'>;
  onConfirm: () => void;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    FinalisationReport,
    MatButton,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinner,
    MatStepperModule,
    MatTableModule,
    SampleStatus,
    SamplesModule,
    TowLogModule,
    TowsModule,
    SharedModule,
    UsersModule,
  ],
  templateUrl: './finalise-tow-dialog.component.html',
  styles: `
    ::ng-deep .mat-horizontal-stepper-header-container {
      pointer-events: none !important;
    }

    .tow-details-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(220px, 1fr));
      gap: 16px;
      margin-top: 8px;
    }

    :host ::ng-deep .tow-details-readonly-field .mdc-notched-outline,
    :host ::ng-deep .tow-details-readonly-field .mdc-notched-outline__leading,
    :host ::ng-deep .tow-details-readonly-field .mdc-notched-outline__notch,
    :host ::ng-deep .tow-details-readonly-field .mdc-notched-outline__trailing {
      border-color: transparent !important;
    }

    :host ::ng-deep .tow-details-readonly-field .mdc-text-field,
    :host ::ng-deep .tow-details-readonly-field .mdc-text-field:hover,
    :host ::ng-deep .tow-details-readonly-field .mdc-text-field--focused,
    :host ::ng-deep .tow-details-readonly-field .mdc-text-field--invalid,
    :host ::ng-deep .tow-details-readonly-field .mdc-text-field--focused:hover {
      background-color: transparent !important;
      box-shadow: none !important;
    }

    :host ::ng-deep .tow-details-readonly-field .mat-mdc-input-element {
      caret-color: transparent;
      cursor: default;
    }

    .sample-stats-row {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
      margin-bottom: 16px;
    }

    .sample-table-wrap {
      overflow: visible;
    }

    .sample-table-wrap table {
      width: 100%;
    }

    @media (max-width: 768px) {
      .tow-details-grid {
        grid-template-columns: 1fr;
      }
    }
  `,
})
export class FinaliseTowDialogComponent {
  @ViewChild('stepper') stepper: MatStepper;
  private static readonly internalIdRegex = /^\d+-\d+(\/\d+)?$/;

  private readonly dialogData = inject<FinaliseTowDialogData>(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef<FinaliseTowDialogComponent>);
  private readonly sampleService = inject(SamplesService);
  private readonly towService = inject(TowsService);
  private readonly matDialog = inject(MatDialog);

  protected tow: IRecordingEvent<'tow'>;
  protected samples: ISample[] = [];
  protected samplesLoading = true;
  protected readonly sampleHeadings = [
    'sampleNo',
    'userId',
    'status',
    'chlorophyllIndex',
    'sampleLatitude',
    'sampleLongitude',
    'sampleMidpointDate',
  ];

  async ngOnInit() {
    this.tow =
      this.dialogData.tow ??
      ((await this.towService.get(this.dialogData.towId)) as unknown as IRecordingEvent<'tow'>);

    const samples = await this.sampleService.getPaginated('', {
      simpleFilters: { towId: `ObjectId:${this.dialogData.towId}` },
      pagination: { pageIndex: 0, pageSize: 999 },
      sort: { active: 'sampleNo', direction: ITableSortDirection.Ascending },
    });
    this.samples = samples.data ?? [];
    this.samplesLoading = false;
  }

  protected get shipName() {
    return this.tow?.metadata?.ship?.name ?? '-';
  }

  protected get towMonthYear() {
    if (!this.tow?.metadata?.month || !this.tow?.metadata?.year) {
      return '-';
    }

    const towDate = this.towService.toDate(this.tow);

    if (!(towDate instanceof Date)) {
      return '-';
    }

    return `${towDate.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })} ${this.tow.metadata.year}`;
  }

  protected get cprId() {
    return this.tow?.metadata?.cpr?.oldCprId ?? '-';
  }

  protected get cprInternalId() {
    const internalId = this.tow?.metadata?.internalId;

    if (!internalId) {
      return '-';
    }

    return FinaliseTowDialogComponent.internalIdRegex.test(internalId) ? internalId : '-';
  }

  protected get validity() {
    return this.tow?.metadata?.validity ?? '-';
  }

  protected get totalSampleCount() {
    return this.samples.length;
  }

  protected get analysedSampleCount() {
    return this.samples.filter((sample) => sample.isAnalysed || sample.analysedAt).length;
  }

  checksValid = false;

  readonly checks: Promise<TowFinalisationCheckResult> = this.sampleService
    .checkTow(this.dialogData.towId)
    .then((checks) => {
      this.checksValid = true;
      return checks;
    })
    .catch((e) => e.error.checks);

  finalising = false;

  finalise() {
    const confirmationDialog = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        confirmationText: 'Are you sure you want to finalise this tow?',
      },
    });
    confirmationDialog
      .afterClosed()
      .pipe(take(1))
      .subscribe((data) => {
        if (data.confirmed) {
          this.finalising = true;
          this.sampleService
            .finaliseTow(this.dialogData.towId)
            .then(() => this.dialogData.onConfirm())
            .finally(() => (this.finalising = false));

          this.matDialogRef.close();
        }
      });
  }

  previousStep() {
    if (this.stepper.selectedIndex >= 1) {
      this.stepper.selectedIndex--;
    }
  }

  nextStep() {
    this.stepper.selectedIndex++;
  }
}
